<template>
  <div class="loading-content">
    <div class="text-center">
      <Loader v-if="loading" class="mx-auto"/>
      <div v-if="showMessage" class="text-dark">{{ message }}</div>
      <div class="text-danger">{{ error }}</div>
    </div>
    <form
      v-if="!loading && authInputRequired && !authDataRequired && !bankIdLoginRequired"
      v-on:submit.prevent="submitCredentials"
    >
      <div
        class="form-inline justify-content-center mb-5"
        v-if="authOptions.length > 1"
      >
        <span class="mr-2">Authentication method</span>
        <select
          class="form-control"
          v-model="authMethod"
        >
          <option
            v-for="opt in authOptions"
            :value="opt.method"
            :key="`auth-method-${opt.method.replace(/\s+/g, '-')}`"
          >{{ opt.title || opt.method }}</option>
        </select>
      </div>
      <div
        class="form mb-5"
        v-if="authCredentials && authCredentials.length"
      >
        <div
          class="form-group row justify-content-center"
          v-for="cred in authCredentials"
          :key="`auth-credential-${cred.name}`"
        >
          <div class="col-sm-6 col-lg-4">
            <div class="input-group">
              <input
                class="form-control"
                :disabled="prefilled"
                :name="`credential-${cred.name}`"
                :required="cred.required"
                :placeholder="cred.title || cred.name"
                :type="cred.visible ? 'text' : 'password'"
                :pattern="cred.template"
                v-model="cred.value"
              >
              <div class="input-group-append">
                <span
                  class="input-group-text px-2 py-1"
                  role="button"
                  v-on:click="$set(cred, 'visible', !cred.visible)"
                ><i class="icon" :class="cred.visible ? 'eye' : 'eye-slash'"></i></span>
              </div>
            </div>
            <small>{{ cred.description }}</small>
          </div>
        </div>
      </div>
      <div class="form-inline justify-content-center">
        <button
          type="submit"
          class="btn btn-theme"
          :disabled="!authCredentialsSet"
        >Authenticate</button>
      </div>
    </form>
    <div v-if="!loading && authDataRequired && !bankIdLoginRequired">
      <p class="text-center">Enter your OTP code with index <strong>{{ authData }}</strong></p>
      <div class="form-inline justify-content-center">
        <input type="text" name="otpCode" class="form-control" required placeholder="OTP code" v-model="otpCode">
        <button class="btn btn-theme" v-on:click="submitOtpCode">Submit OTP code</button>
      </div>
    </div>
    <div v-if="!loading && userConsentRequired">
      <div class="row justify-content-center mb-5">
        <div class="col-12 col-md-8 col-lg-6">
          <div v-if="service === 'AIS'">
            <p class="text-justify">
              Authentication is initiated by <strong>{{ appName }}</strong>.
              After you complete authentication, your payment accounts data will be shared with the authentication initiator.
              By pressing "Continue authentication" button you agree with the <a href="/terms" target="_blank">terms of service</a>.
              Press "Cancel" button if you are not willing to share your payment accounts data.
            </p>
          </div>
          <div v-else-if="service === 'PIS'">
            <p><strong>Payment details</strong></p>
            <p>Payment ID provided by the merchant: <strong>{{ paymentInfo.endToEndIdentification }}</strong></p>
            <p>Account number where to transfer funds: <strong>{{ paymentInfo.creditorAccount.identification }}</strong></p>
            <p>Payee name in the payment: <strong>{{ paymentInfo.creditorName }}</strong></p>
            <p v-if="paymentInfo.creditorAddress && paymentInfo.creditorAddress.country">Payee country: <strong>{{ countryNames[paymentInfo.creditorAddress.country] || paymentInfo.creditorAddress.country }}</strong></p>
            <p>Payment amount: <strong>{{ paymentInfo.amount }} {{ paymentInfo.currency }}</strong></p>
            <p>Message or reference number in the payment: <strong>{{ paymentInfo.remittanceInformation }}</strong></p>
            <p class="text-justify">
              Payment is initiated by <strong>{{ appName }}</strong>.
              After you complete authentication, your payment consent will be requested by your account servicing payment service provider.
              By pressing "Continue authentication" button you agree with the <a href="/terms" target="_blank">terms of service</a>.
              Press "Cancel" button if you are not willing to complete this payment.
            </p>
          </div>
          <div class="form-inline justify-content-center">
            <button class="btn btn-theme" v-on:click="submitDataSharingConsent">Continue authentication</button>
          </div>
          <div class="text-center">
            <button class="btn btn-link" v-on:click="cancelAuth">Cancel</button>
          </div>
        </div>
      </div>
      <div class="row justify-content-center">
        <div class="col-12 col-md-8 col-lg-6 small text-muted">
          <p><strong>About {{ appName }}</strong></p>
          <p class="text-justify">{{ appDescription }}</p>
          <p><strong>About this service</strong></p>
          <p class="text-justify">TBD</p>
        </div>
      </div>
    </div>
    <div v-if="!loading && bankIdLoginRequired">
      <p class="text-center">Start the BankID app on your mobile device and tap the QR code button in the app to scan
        the QR code on this page</p>
      <div class="qrcode-container">
        <canvas id="qrcode" class="qrcode-image"></canvas>
      </div>
    </div>
    <div class="text-center" v-if="!userConsentRequired && !loading">
      <button class="btn btn-link" v-on:click="cancelAuth">Cancel</button>
    </div>
  </div>
</template>

<script>
import constants from '../constants.js'
import Loader from '../components/elements/Loader.vue'
import BaseService from '../services/BaseService.js'
import QRCode from 'qrcode'

export default {
  name: 'home',
  components: {
    Loader
  },
  data () {
    return {
      countryNames: constants.countriesNames,
      error: '',
      loading: true,
      message: '',
      service: null,
      prevStatus: null,
      status: null,
      aspspName: '',
      authMethod: '', // authentication method selected by a user
      authOptions: [],
      authData: '',
      authDataRequired: false,
      otpCode: '',
      appName: '',
      appDescription: '',
      paymentInfo: null,
      userConsentRequired: false,
      bankIdLoginRequired: false,
      updates: 0,
      _prefilled: null,
      showMessage: true
    }
  },
  computed: {
    authInputRequired: function () {
      return this.authOptions.length > 1 || (
        this.authOptions.length === 1 &&
        this.authOptions[0].info &&
        this.authOptions[0].info.credentials &&
        this.authOptions[0].info.credentials.length
      )
    },
    authOption: function () {
      return this.authOptions.find(opt => (opt.method || '') === this.authMethod)
    },
    authCredentials: function () {
      return (this.authOption && this.authOption.info && this.authOption.info.credentials) || []
    },
    authCredentialsSet: function () {
      for (const cred of this.authCredentials) {
        if (cred.required && !cred.value) {
          return false
        }
      }
      return true
    },
    prefilled: function () {
      // evaluated only once
      if (this._prefilled === null || this._prefilled === undefined) {
        this._prefilled = !!this.authCredentialsSet
      }
      return this._prefilled
    }
  },
  methods: {
    async handleWaitingConsentConfirmation (data) {
      this.appName = data.applicationName
      this.appDescription = data.applicationDescription
      this.userConsentRequired = true
      this.paymentInfo = data.paymentInfo
      this.loading = false
    },
    async handleNotReady (data) {
      this.aspspName = data.aspspName
      this.authOptions = data.authInfo
      this.authMethod = (this.authOptions[0] && this.authOptions[0].method) || ''
      if (this.authInputRequired) {
        this.loading = false
      } else {
        this.message = 'Starting authorization'
        const r = await BaseService.startAuthorization()
        console.log(r)
        this.scheduleUpdate(1000)
      }
    },
    async handleInvalid (data) {
      this.error = 'Unable to authenticate'
      if (data.redirectUri) {
        this.message = 'Returning to the authentication initiator'
        this.redirect(data.redirectUri)
      } else {
        this.message = 'You need to close this window'
      }
    },
    async handleCancelled (data) {
      this.error = 'Authentication cancelled'
      if (data.redirectUri) {
        this.message = 'Returning to the authentication initiator'
        this.redirect(data.redirectUri)
      } else {
        this.message = 'You need to close this window'
      }
    },
    async handleWaitingAspspReturn (data) {
      this.message = ''
      try {
        const r = await BaseService.continueAuthorization({ redirectUri: window.location.href })
        console.log(r)
      } catch (e) {
        throw new Error('Unable to continue authorization')
      } finally {
        this.scheduleUpdate(500)
      }
    },
    async handleReturnedFromAspsp (data) {
      // continue authorization is already started. Just wait
      this.scheduleUpdate(500)
    },
    async handleAspspRedirect (data) {
      this.message = 'Redirecting to your account servicing provider'
      const r = await BaseService.notifyLeaving()
      if (r.data.message !== 'OK') {
        throw new Error(r.data.message)
      }
      this.redirect(data.redirectUri)
    },
    async handleWaitingUserDecoupledAuth (data) {
      if (data.authData) {
        this.authDataRequired = true
        this.authData = data.auth_data
        this.otpCode = ''
        this.loading = false
      } else {
        if (data.redirectUri) { // QR code generation flow
          const bankIdURI = data.redirectUri.split('&')[0] // remove redirect
          this.bankIdLoginRequired = true
          this.loading = false
          this.message = ''
          this.$nextTick(() => {
            var canvasElement = document.getElementById('qrcode')
            canvasElement.style.display = 'block'
            QRCode.toCanvas(canvasElement, bankIdURI, {}, function (err, canvas) {
              if (err) throw err
            })
          })
          try {
            const r = await BaseService.continueAuthorization()
            console.log(r)
          } catch (e) {
            throw new Error('Unable to continue decoupled authorization')
          }
          this.scheduleUpdate(1000, false)
        } else {
          this.message = 'To continue please use your authentication app'
          try {
            const r = await BaseService.continueAuthorization()
            console.log(r)
          } catch (e) {
            throw new Error('Unable to continue decoupled authorization')
          }
          this.scheduleUpdate(500)
        }
      }
    },
    async handleApplicationRedirect (data) {
      this.message = 'Returning to the authentication initiator'
      this.redirect(data.redirectUri)
    },
    async handleAuthStarted (data) {
      // just wait
      this.scheduleUpdate(500)
    },
    redirect (redirectUrl) {
      setTimeout(() => {
        window.location.href = redirectUrl
      }, 500)
    },
    async updateStatus () {
      try {
        this.updates += 1
        const r = await BaseService.getSession()
        this.error = ''
        this.showMessage = r.data.showMessage
        this.prevStatus = this.status
        this.service = r.data.service
        this.status = r.data.status
        const handlerName = this.status
          .split('_')
          .map(e => e.substr(0, 1) + e.substr(1).toLowerCase())
          .join('')
        const handler = this[`handle${handlerName}`]
        if (!handler) {
          this.message = ''
          throw new Error(`Unable to handle authentication ${this.status}`)
        }
        handler(r.data)
      } catch (e) {
        let retry = true
        if (e.response) {
          if (e.response.status === 404) {
            this.error = 'Unable to access server'
            retry = false
          } else if (e.response.status === 422
            // && // this code is related to tilisy, so commenting it
            //e.response.data &&
            // e.response.data.detail &&
            // e.response.data.detail.find(d =>
            //  d.loc[0] === 'cookie' &&
            //  d.loc[1] === 'sessionid' &&
            //  d.type === 'value_error.missing')
          ) {
            try {
              await BaseService.restoreSession(window.location.href)
            } catch (e) {
              this.error = 'Authentication session has expired'
            }
          } else {
            this.error = 'Error while updating authentication status'
          }
        } else if (e.message) {
          this.error = e.message
        } else {
          this.error = 'Unknown error occurred'
        }
        this.loading = retry
        if (retry) {
          this.scheduleUpdate(500)
        }
      }
    },
    scheduleUpdate (timeout, loading = true) {
      this.loading = loading
      if (this.updates > 5) {
        timeout = timeout * Math.ceil(this.updates / 5)
      }
      setTimeout(this.updateStatus, timeout)
    },
    async submitCredentials () {
      this.loading = true
      try {
        const r = await BaseService.startAuthorization(
          this.authMethod,
          this.authCredentials.map(c => c.value || '')
        )
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(500)
    },
    async submitOtpCode () {
      this.loading = true
      try {
        const r = await BaseService.continueAuthorization({ code: this.otpCode })
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(500)
    },
    async submitDataSharingConsent () {
      this.loading = true
      try {
        const r = await BaseService.confirmConsent()
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.userConsentRequired = false
      this.scheduleUpdate(500)
    },
    async cancelAuth () {
      this.loading = true
      try {
        const r = await BaseService.cancelAuth()
        console.log(r)
      } catch (e) {
        console.error(e)
      }
      this.scheduleUpdate(500)
    }
  },
  mounted () {
    this.updateStatus()
  }
}
</script>

<style lang="scss" scoped>
.loading-content {
  padding-top: 20vh;
}

.qrcode-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.qrcode-image {
  border: 1px solid #ccc;
}

.icon {
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  width: 1rem;
  height: 1rem;
}

.eye {
  background-image: url(../assets/eye.svg);
}

.eye-slash {
  background-image: url(../assets/eye-slash.svg);
}
</style>
